import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import LoadingButton from 'atoms/LoadingButton';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react-lite';
import FormInputText from 'molecules/FormField/FormInputText';
import PasswordField from 'molecules/FormField/PasswordField';
import FormTitle from 'molecules/Registration/FormHeader';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { IAuthData, signIn } from 'utils/authUtils';
import { errorMessage, validateEmail, validatePassword } from 'utils/errorUtils';
import { Resources } from 'utils/resources';

import s from '../ConnectForm.module.scss';

const SignInForm = ({
  email,
  sponsorId,
  requestPending,
  setRequestPending,
  onStateChange
}: {
  email?: string;
  sponsorId?: string;
  requestPending?: boolean;
  setRequestPending: (pending: boolean) => void;
  onStateChange: (state: string, data?: IAuthData) => Promise<void>;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    control
  } = useForm({
    defaultValues: { email: email ?? '', password: '' }
  });

  useEffect(() => {
    clearErrors('email');
    clearErrors('password');

    register('email', {
      // required: 'You must enter a valid email',
      validate: (value) => {
        return !value || validateEmail(value as string) || 'Email is not valid';
      }
    });

    register('password', {
      // required: 'You must enter a valid password',
      validate: (value) => {
        return !value || validatePassword(value as string) || 'Password is not valid';
      }
    });
  }, [register, clearErrors]);

  const onError = (err: Error | string) => {
    console.warn('SignInForm - onError', err);
    setRequestPending(false);
    setError('password', {
      type: 'manual',
      message: errorMessage(err, 'Request Failed')
    });
  };

  const onSuccess = async (state: string, data: IAuthData) => {
    await onStateChange(state, data);
  };

  const onSignIn = (data: Record<string, string>) => {
    if (!isEmpty(errors)) return;

    if (!validateEmail(data.email)) {
      setError('email', {
        type: 'manual',
        message: 'You must enter a valid email'
      });
      return;
    }

    if (!validatePassword(data.password)) {
      setError('password', {
        type: 'manual',
        message: 'You must enter a valid password'
      });
      return;
    }

    setRequestPending(true);
    clearErrors();

    signIn(data.email, data.password, onSuccess, onError);
  };

  return (
    <div className={s.ConnectForm}>
      <FormTitle
        title={Resources.REGISTRATION_SIGN_IN_TITLE}
        subTitle={Resources.REGISTRATION_SIGN_IN_SUBTITLE}
      />
      <form id="signIn" className={s.ConnectForm__form} onSubmit={handleSubmit(onSignIn)}>
        <div className={s.ConnectForm__field}>
          <FormInputText
            control={control}
            name="email"
            type="email"
            autoFocus={true}
            description="Email"
            defaultValue={email}
            disabled={requestPending}
          />
        </div>
        <div className={s.ConnectForm__field}>
          <PasswordField
            name="password"
            control={control}
            description="Password"
            disabled={requestPending}
          />
        </div>
        <LoadingButton
          className={s.ConnectForm__submit__btn}
          loading={requestPending}
          type="submit"
        >
          Log in
        </LoadingButton>
        {/*<GoogleSignIn*/}
        {/*  requestPending={requestPending}*/}
        {/*  setRequestPending={setRequestPending}*/}
        {/*  onStateChange={onStateChange}*/}
        {/*  onError={onError}*/}
        {/*/>*/}
        <Divider />
        <Typography variant="caption" color="text.secondary" mt={1}>
          {'Forgot your password? '}
          <Link component="button" color="inherit" onClick={() => onStateChange('forgotPassword')}>
            Reset password
          </Link>
        </Typography>
        <Typography variant="caption" component="div" color="text.secondary" mt={1}>
          {'New to USREM? '}
          <Link component="button" color="inherit" onClick={() => onStateChange('signUp')}>
            Create a new account
          </Link>
        </Typography>
      </form>
    </div>
  );
};

export default observer(SignInForm);
